export const FOUNDER_SUPPORT_SESSION_STORAGE_KEY = 'xprojex_founder_support_session';
export const FOUNDER_SUPPORT_SESSION_HEADER = 'X-XProjex-Support-Session';

const FOUNDER_SUPPORT_READ_PATHS = [
  /^\/dashboard\/?$/,
  /^\/report\/[^/]+\/?$/,
  /^\/artifacts\/[^/]+\/?$/,
  /^\/impact-evaluator\/[^/]+\/?$/,
  /^\/wayfinder\/[^/]+\/?$/,
  /^\/safeguard\/[^/]+\/?$/,
];

export interface FounderSupportSession {
  id: string;
  token: string;
  actorEmail: string | null;
  targetUserId: string;
  targetEmail: string;
  targetPlan: string;
  reason: string;
  status: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
}

const isSession = (value: unknown): value is FounderSupportSession => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const session = value as Record<string, unknown>;
  return typeof session.id === 'string'
    && typeof session.token === 'string'
    && typeof session.targetUserId === 'string'
    && typeof session.targetEmail === 'string'
    && typeof session.targetPlan === 'string'
    && typeof session.reason === 'string'
    && typeof session.startedAt === 'string'
    && typeof session.expiresAt === 'string';
};

export function clearFounderSupportSession(): void {
  try {
    window.sessionStorage.removeItem(FOUNDER_SUPPORT_SESSION_STORAGE_KEY);
  } catch {
    // The server still enforces expiry if session storage is unavailable.
  }
}

export function readFounderSupportSession(): FounderSupportSession | null {
  try {
    const raw = window.sessionStorage.getItem(FOUNDER_SUPPORT_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const value = JSON.parse(raw) as unknown;
    if (!isSession(value) || Date.parse(value.expiresAt) <= Date.now()) {
      clearFounderSupportSession();
      return null;
    }
    return value;
  } catch {
    clearFounderSupportSession();
    return null;
  }
}

export function storeFounderSupportSession(
  payload: { session?: Omit<FounderSupportSession, 'token'>; token?: string } | null,
): FounderSupportSession | null {
  const session = payload?.session && payload.token
    ? { ...payload.session, token: payload.token }
    : null;
  if (!isSession(session)) return null;
  try {
    window.sessionStorage.setItem(
      FOUNDER_SUPPORT_SESSION_STORAGE_KEY,
      JSON.stringify(session),
    );
  } catch {
    return null;
  }
  return session;
}

export function isFounderSupportReadPath(pathname: string): boolean {
  return FOUNDER_SUPPORT_READ_PATHS.some((pattern) => pattern.test(pathname));
}

export function isFounderSupportView(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return isFounderSupportReadPath(window.location.pathname)
    && params.get('support') === 'true';
}

export function getFounderSupportRedirect(
  pathname: string,
  search: string,
  hasStoredSession: boolean,
): string | null {
  const supportView = isFounderSupportReadPath(pathname)
    && new URLSearchParams(search).get('support') === 'true';

  if (supportView && !hasStoredSession) return '/admin/god-eye';
  if (hasStoredSession && !supportView) return '/dashboard?support=true';
  return null;
}

export function buildFounderSupportRoute(route: string): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const resolved = new URL(route, window.location.origin);
    if (resolved.origin !== window.location.origin) return null;
    if (!isFounderSupportReadPath(resolved.pathname)) return null;
    resolved.searchParams.set('support', 'true');
    return `${resolved.pathname}${resolved.search}${resolved.hash}`;
  } catch {
    return null;
  }
}

export function getFounderSupportSessionHeader(): Record<string, string> {
  const session = readFounderSupportSession();
  return session ? { [FOUNDER_SUPPORT_SESSION_HEADER]: session.token } : {};
}
